import { useState, forwardRef, useEffect, useCallback } from 'react'
import { Popover } from 'reactstrap'
import classnames from 'classnames'
import moment from 'moment'
import Select from '../../common/form/Select'
import DatePicker from 'react-datepicker'
import { ReactComponent as FilterIcon } from '../../../assets/images/common/icons/filter-icon.svg'
import { ReactComponent as DropDownIcon } from '../../../assets/images/common/icons/select-drop-down-icon-light-navy.svg'
import styles from './LeaderboardFilters.module.scss'
import SubmitButton from '../../common/buttons/SubmitButton'
import { useDispatch, useSelector } from 'react-redux'
import { getCommunityCategories } from '../../../actions/programCategoryActions'
import { initialFilters, setLeaderboardFilters } from '../../../reducers/dashboardReducer'
import { getProgramsFilterOptions } from '../../../actions/programActions'

// eslint-disable-next-line react/prop-types
const DatePickerInput = forwardRef(({ value, onClick }, ref) => (
  <button className={styles.datePickerInput} onClick={onClick} ref={ref}>
    {value}
    <DropDownIcon/>
  </button>
))

DatePickerInput.displayName = 'DatePickerInput'

const currentDate = new Date()

const getTitleText = (startDate, endDate) => {
  const startDateMoment = moment(startDate)
  const endDateMoment = moment(endDate)
  const startThisYear = moment(currentDate).startOf('year')
  const endThisYear = moment(currentDate).endOf('year')
  const startLastYear = moment(currentDate).subtract(1, 'year').startOf('year')
  const endLastYear = moment(currentDate).subtract(1, 'year').endOf('year')

  if (startDateMoment.isSame(startThisYear, 'day') && endDateMoment.isSame(endThisYear, 'day')) {
    return `This Year (${currentDate.getFullYear()})`
  }

  if (startDateMoment.isSame(startLastYear, 'day') && endDateMoment.isSame(endLastYear, 'day')) {
    return `Last Year (${currentDate.getFullYear() - 1})`
  }

  if (endDateMoment.isSame(moment(currentDate), 'day')) {
    const ranges = { 20: 'Last 20 Days', 30: 'Last 30 Days', 31: 'Last 30 Days', 60: 'Last 60 Days', 90: 'Last 90 Days' }
    const diffDays = endDateMoment.diff(startDateMoment, 'days') + 1
    if (ranges[diffDays]) {
      return ranges[diffDays]
    }
  }

  return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
}

const LeaderboardFilters = () => {
  const dispatch = useDispatch()
  const {
    leaderboardFilters,
    communityCategories,
    loadingCategories,
    optionCourses,
    loadingOptionCourses,
    clearFiltersCounter,
  } = useSelector(state => state.dashboard)
  const [isFiltersMenuOpen, setFiltersMenuOpen] = useState(false)
  const [filters, setFilters] = useState(leaderboardFilters)
  const [filtersTitle, setFiltersTitle] = useState()
  const [savedFiltersCounter, setSavedFiltersCounter] = useState(clearFiltersCounter)

  const courses = filters.category ? optionCourses.filter(course => course.categoryId === filters.category.value) : optionCourses
  const savedProgram = optionCourses.find(course => course.value === leaderboardFilters.courseId)
  const savedCategory = communityCategories.find(category => category.value === leaderboardFilters.categoryId)
  const extraFiltersCounter = [filters.category, filters.program].reduce((acc, filter) => acc + (filter ? 1 : 0), 0)

  useEffect(() => {
    dispatch(getCommunityCategories())
    dispatch(getProgramsFilterOptions({ completed: true, active: true }))
  }, [dispatch])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      category: savedCategory,
      program: savedProgram,
    }))
  }, [savedCategory, savedProgram])

  useEffect(() => {
    setFiltersTitle(getTitleText(filters.startDate, filters.endDate))
  }, [filters.startDate, filters.endDate])

  const handleOpenFiltersMenuClick = () => {
    if (isFiltersMenuOpen){
      setFilters({
        startDate: leaderboardFilters.startDate,
        endDate: leaderboardFilters.endDate,
        category: savedCategory,
        program: savedProgram,
      })
    }
    setFiltersMenuOpen(value => !value)
  }

  const handleFixedRangeChanged = (range) => {
    if (range > 90){
      setFilters({ ...filters, startDate: new Date(range, 0, 1), endDate: new Date(range, 11, 31) })
    } else {
      const rangeDate = new Date()
      setFilters({
        ...filters,
        startDate: new Date(rangeDate.setDate(rangeDate.getDate() - range)),
        endDate: currentDate,
      })
    }
  }

  const handleApplyFilters = () => {
    const requestFilters = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      categoryId: filters.category?.value,
      courseId: filters.program?.value,
    }
    handleFiltersChanged(requestFilters)
  }

  const handleFiltersChanged = useCallback((filters) => {
    dispatch(setLeaderboardFilters(filters))
    setFiltersMenuOpen(false)
  }, [dispatch])

  const handleClearFilters = useCallback(() => {
    setFilters(initialFilters)
    handleFiltersChanged(initialFilters)
  }, [handleFiltersChanged])

  useEffect(() => {
    if (clearFiltersCounter !== savedFiltersCounter) {
      setSavedFiltersCounter(clearFiltersCounter)
      handleClearFilters()
    }
  }, [clearFiltersCounter, handleClearFilters, savedFiltersCounter])

  return (
    <>
      <button
        id='leaderboardFiltersButton'
        className={classnames('flex-grow-1 flex-lg-grow-0 d-flex align-items-center', styles.filtersButton)}
        onClick={handleOpenFiltersMenuClick}
      >
        <FilterIcon className='mx-2' />
        {filtersTitle}
        {extraFiltersCounter > 0 && <span className={styles.extraFilter}>+ {extraFiltersCounter} filter(s)</span>}
      </button>
      <Popover
        target='leaderboardFiltersButton'
        isOpen={isFiltersMenuOpen}
        placement='auto'
        trigger='legacy'
        toggle={handleOpenFiltersMenuClick}
        popperClassName={styles.filtersMenu}
      >
        <div className={styles.filtersBody}>
          <div className={styles.fixedRange}>
            <div className={styles.filtersTitle}>Fixed Range</div>
            <button className={styles.rangeButton} onClick={() => handleFixedRangeChanged(90)} >Last 90 Days</button>
            <button className={styles.rangeButton} onClick={() => handleFixedRangeChanged(60)}>Last 60 Days</button>
            <button className={styles.rangeButton} onClick={() => handleFixedRangeChanged(30)}>Last 30 Days</button>
            <button className={styles.rangeButton} onClick={() => handleFixedRangeChanged(20)}>Last 20 Days</button>
            <button className={styles.rangeButton} onClick={() => handleFixedRangeChanged(currentDate.getFullYear())}>
              This Year ({currentDate.getFullYear()})
            </button>
            <button className={styles.rangeButton} onClick={() => handleFixedRangeChanged(currentDate.getFullYear() - 1)}>
              Last Year ({currentDate.getFullYear() - 1})
            </button>
          </div>
          <div className={styles.filters}>
            <div className={styles.filtersTitle}>
              Leaderboard Filters
              <button className={styles.closeButton} onClick={() => setFiltersMenuOpen(false)}>
                <i className='ml-4 fa fa-close' />
              </button>
            </div>
            <div className={styles.dates}>
              <div>
                <label className={styles.dateLabel} htmlFor='startDatePicker'>Start Date</label>
                <DatePicker
                  id='startDatePicker'
                  selected={filters.startDate}
                  customInput={<DatePickerInput/>}
                  onChange={(date) => setFilters((prev) => ({ ...prev, startDate: date }))}
                  dateFormat='MMMM, d eee'
                />
              </div>
              <div>
                <label className={styles.dateLabel} htmlFor='endDatePicker'>End Date</label>
                <DatePicker
                  id='endDatePicker'
                  selected={filters.endDate}
                  customInput={<DatePickerInput/>}
                  onChange={(date) => setFilters((prev) => ({ ...prev, endDate: date }))}
                  dateFormat='MMMM, d eee'
                />
              </div>
            </div>
            <div>
              <label className={styles.dateLabel} htmlFor='categorySelect'>Program Category</label>
              <Select
                id='categorySelect'
                isSearchable={false}
                placeholder='Choose a Program Category'
                options={communityCategories}
                value={filters.category}
                classNames={{
                  menuList: () => styles.selectMenuList,
                }}
                onChange={option => setFilters({ ...filters, category: option })}
                isClearable
                isLoading={loadingCategories}
              />
            </div>
            <div className='mt-3'>
              <label className={styles.dateLabel} htmlFor='programSelect'>Program Title</label>
              <Select
                id='programSelect'
                isSearchable={true}
                placeholder='Choose a Program Title'
                options={courses}
                value={filters.program}
                classNames={{
                  menuList: () => styles.selectMenuList,
                }}
                onChange={option => setFilters({ ...filters, program: option })}
                isClearable
                isLoading={loadingOptionCourses}
              />
            </div>
            <div className={styles.buttons}>
              <button
                className={classnames('ml-3 w-100 mt-3 animation-shrink d-flex align-items-center justify-content-center', styles.resetFiltersButton)}
                onClick={handleClearFilters}
              >
                Clear <i className='ml-4 fa fa-close' />
              </button>
              <SubmitButton buttonMessage='Apply' className='mt-3' onClick={handleApplyFilters}/>
            </div>
          </div>
        </div>
      </Popover>
    </>
  )
}

export default LeaderboardFilters
